import { MouseEvent, useEffect, useRef, useState } from 'react';

import { Icon } from '@cds/icon';

import type { SuggestedMemoType } from '../../types/ai-panel.types';

import * as styles from './suggested-memo-list.css';

interface SuggestedMemoListProps {
  memos: SuggestedMemoType[];
  onToggleMemo: (memo: SuggestedMemoType) => void;
  onOpenMemo?: (memo: SuggestedMemoType) => void;
}

const SuggestedMemoList = ({
  memos,
  onToggleMemo,
  onOpenMemo,
}: SuggestedMemoListProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const iconColor = isOpen ? 'blue500' : 'grey700';

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (e: PointerEvent) => {
      const container = containerRef.current;
      if (!container || container.contains(e.target as Node)) return;

      setIsOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className={styles.container}>
      <button
        type="button"
        className={styles.listContainer({ isOpen })}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
      >
        <Icon name="ic_ai" size={32} color={iconColor} />
        <span className={styles.title}>AI 추천 메모</span>
        <div className={styles.chevronButton({ isOpen })}>
          <Icon name="ic_chevron_down" size={20} color={iconColor} />
        </div>
      </button>
      <ul className={styles.itemsContainer({ isOpen })}>
        {memos.map((memo) => (
          <SuggestedMemoItem
            key={memo.memoId}
            memo={memo}
            onToggleMemo={() => onToggleMemo(memo)}
            onOpenMemo={() => onOpenMemo?.(memo)}
          />
        ))}
      </ul>
    </div>
  );
};

interface SuggestedMemoItemProps {
  memo: SuggestedMemoType;
  onToggleMemo: () => void;
  onOpenMemo: () => void;
}

const SuggestedMemoItem = ({
  memo,
  onToggleMemo,
  onOpenMemo,
}: SuggestedMemoItemProps) => {
  const { title, isSelected } = memo;

  const handleToggleMemo = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onToggleMemo();
  };

  return (
    <li className={styles.itemContainer}>
      <button
        type="button"
        className={styles.openMemo}
        aria-label={`${title} 메모 열기`}
        onClick={onOpenMemo}
      >
        <Icon name="ic_memo" size={24} color="grey700" />
        <span className={styles.memo}>{title}</span>
      </button>
      <button
        type="button"
        aria-label={
          isSelected
            ? `${title} 추천 메모 선택 해제`
            : `${title} 추천 메모 추가`
        }
        aria-pressed={isSelected}
        className={styles.addMemo({ isSelected })}
        onClick={handleToggleMemo}
      >
        <span>{isSelected ? '추가됨' : '추가하기'}</span>
        {isSelected ? (
          <Icon name="ic_check" size={16} color="white" />
        ) : (
          <Icon name="ic_plus" size={16} color="grey700" />
        )}
      </button>
    </li>
  );
};

export default SuggestedMemoList;
